import moveSharedFolderItems from '../actions/moveSharedFolderItems';
import { orchestrator } from 'satcheljs';
import datapoints from '../actions/datapoints';
import createItemContextsForDeleteStoreUpdate from 'owa-mail-triage-action/lib/actions/helpers/createItemContextsForDeleteStoreUpdate';
import deleteItemsStoreUpdate from 'owa-mail-actions/lib/triage/deleteItemsStoreUpdate';
import { getSelectedTableView, TableView, getRowIdsFromRowKeys } from 'owa-mail-list-store';
import deleteItemService from 'owa-mail-store/lib/services/deleteItemService';
import * as undoActions from 'owa-mail-undo';
import DeleteItemResponseMessage from 'owa-service/lib/contract/DeleteItemResponseMessage';
import tombstoneOperations from 'owa-mail-list-tombstone';
import showNotificationOnMove from 'owa-mail-triage-action/lib/actions/move/helpers/showNotificationOnMove';
import copyItemsService from '../services/copyItemsService';
import getServiceContextFolderId from 'owa-mail-store/lib/services/utils/getServiceContextFolderId';
import { logUsage } from 'owa-analytics';
import ItemInfoResponseMessage from 'owa-service/lib/contract/ItemInfoResponseMessage';
import reloadTable from 'owa-mail-triage-table-load-extra/lib/actions/reloadTable';
import { updateCountForItemMove } from '../actions/move/helpers/updateFolderCountForArchiveMailbox';
import * as trace from 'owa-trace';
import { showNotificationOnMoveFailure } from 'owa-mail-filterable-menu-behavior';

// Exporting this as InstrumentationContext is private for this file

export default orchestrator(moveSharedFolderItems, async actionMessage => {
    const {
        rowKeys,
        originalTableViewId,
        destinationFolderId,
        parentFolderId,
        actionSource,
    } = actionMessage;

    const rowIdStrings = getRowIdsFromRowKeys(rowKeys, originalTableViewId);
    const itemContexts = createItemContextsForDeleteStoreUpdate(rowIdStrings);
    const tableView = getSelectedTableView();

    logUsage(
        datapoints.moveItems.name(rowKeys, destinationFolderId, tableView, actionSource),
        datapoints.moveItems.customData(rowKeys, destinationFolderId, tableView, actionSource)
    );

    // Update folder count manually for shared folder
    updateCountForItemMove(itemContexts, destinationFolderId);
    deleteItemsStoreUpdate(itemContexts, parentFolderId, 'Move');
    undoActions.clearLastUndoableAction();

    const serviceItemIds = rowIdStrings.map(itemIdValue => {
        return {
            Id: itemIdValue,
        };
    });

    try {
        // Make copy service request
        // Wait for the copy service call to make further delete service call
        const copyResponse = await copyItemsService(
            rowIdStrings,
            getServiceContextFolderId(destinationFolderId),
            true /* RemoteExecute */
        );
        const responseMessage = copyResponse.ResponseMessages?.Items
            ? (copyResponse.ResponseMessages.Items[0] as ItemInfoResponseMessage)
            : null;
        if (responseMessage && responseMessage.ResponseClass == 'Success') {
            // Make delete conversation call
            const deleteResponse = await deleteItemService(
                serviceItemIds,
                'HardDelete' /* disposalType */,
                null /* requestInitOptions */
            );

            /*
             * Items are removed from the Tombstone regardless of the api response and in the case failure, table is also reloaded.
             * This is done to avoid infinite loading bug, which occurs if delete api fails for shared folder or
             * server notification falis for primary mailbox
             */
            removeFromTombStone(tableView, rowKeys);
            const responseItems = <DeleteItemResponseMessage[]>deleteResponse;
            if (responseItems[0].ResponseClass !== 'Success') {
                reloadTable(tableView);
                // Show failure notification for the error if delete api fails
                showNotificationOnMoveFailure(destinationFolderId);
            } else {
                // Show move conversation notification for shared folder only after both the apis return with success
                showNotificationOnMove(
                    rowIdStrings.length,
                    destinationFolderId,
                    false /* allowUndo */
                );
            }
        } else {
            /**
             * Items are removed from the Tombstone and table is reloaded if the copyItems service fails
             */
            removeFromTombStone(tableView, rowKeys);
            reloadTable(tableView);
            // Show failure notification for the error if copy api fails
            showNotificationOnMoveFailure(destinationFolderId);
        }
    } catch (error) {
        trace.errorThatWillCauseAlert(
            'Shared Folder move item action failed : ' + JSON.stringify(error)
        );
    }

    // Show move item notification for shared folder, as move items action for shared folder calls delete items action
});

/**
 * Removes rowkeys from the tombstone for the given tableview
 * @param tableView - selected tableview
 * @param rowKeys - rowKeys to remove from tombstone
 */
export const removeFromTombStone = (tableView: TableView, rowKeys: string[]) => {
    const folderId = tableView.tableQuery.folderId;
    tombstoneOperations.removeRowsFromTombstone(rowKeys, folderId);
};
export { InstrumentationContext } from 'owa-search';
